import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUpIcon, TrendingDownIcon, MinusIcon } from 'lucide-react';
import { Card } from '../ui/Card';
export interface MetricCardProps {
  label: string;
  value: string | number;
  change?: {
    value: number;
    trend: 'up' | 'down' | 'neutral';
  };
  icon?: React.ReactNode;
  iconColor?: string;
  className?: string;
}
export function MetricCard({
  label,
  value,
  change,
  icon,
  iconColor = 'bg-primary-100 text-primary-600',
  className = ''
}: MetricCardProps) {
  const getTrendIcon = () => {
    if (!change) return null;
    switch (change.trend) {
      case 'up':
        return <TrendingUpIcon className="w-4 h-4" data-id="element-89" />;
      case 'down':
        return <TrendingDownIcon className="w-4 h-4" data-id="element-90" />;
      default:
        return <MinusIcon className="w-4 h-4" data-id="element-91" />;
    }
  };
  const getTrendColor = () => {
    if (!change) return '';
    switch (change.trend) {
      case 'up':
        return 'text-success-600 bg-success-50';
      case 'down':
        return 'text-error-600 bg-error-50';
      default:
        return 'text-surface-600 bg-surface-100';
    }
  };
  return <Card variant="elevated" padding="md" className={className} data-id="element-92">
      <div className="flex items-start justify-between" data-id="element-93">
        <div className="flex-1" data-id="element-94">
          <p className="text-body-sm text-surface-500 mb-1" data-id="element-95">{label}</p>
          <motion.p initial={{
          opacity: 0,
          y: 5
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-display-sm text-surface-900 tabular-nums" data-id="element-96">
            {value}
          </motion.p>
          {change && <div className="flex items-center gap-1 mt-2" data-id="element-97">
              <span className={`
                  inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-caption font-medium
                  ${getTrendColor()}
                `} data-id="element-98">
                {getTrendIcon()}
                {change.value > 0 ? '+' : ''}
                {change.value.toFixed(1)}%
              </span>
              <span className="text-caption text-surface-500" data-id="element-99">
                vs last period
              </span>
            </div>}
        </div>
        {icon && <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconColor}`} data-id="element-100">
            {icon}
          </div>}
      </div>
    </Card>;
}
// Grid of metric cards
export interface MetricGridProps {
  metrics: MetricCardProps[];
  columns?: 2 | 3 | 4;
  className?: string;
}
export function MetricGrid({
  metrics,
  columns = 4,
  className = ''
}: MetricGridProps) {
  const gridCols = {
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
  };
  return <div className={`grid ${gridCols[columns]} gap-4 ${className}`} data-id="element-101">
      {metrics.map((metric, index) => <motion.div key={metric.label} initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: index * 0.1
    }} data-id="element-102">
          <MetricCard {...metric} data-id="element-103" />
        </motion.div>)}
    </div>;
}